import React, { useEffect, useRef, useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Box,
} from '@chakra-ui/react';
import * as maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import * as turf from '@turf/turf';
import { Point } from '../types';

interface TaksasiJalurMapModalProps {
  isOpen: boolean;
  onClose: () => void;
  jalurData: {
    points: Point[];
    title: string;
  } | null;
}

const TaksasiJalurMapModal: React.FC<TaksasiJalurMapModalProps> = ({
  isOpen,
  onClose,
  jalurData,
}) => {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<maplibregl.Map | null>(null);

  useEffect(() => {
    let mapInstance: maplibregl.Map | null = null;
    let initTimer: ReturnType<typeof setTimeout>;

    if (isOpen) {
      // Delay initialization so Modal animation paints dimensions first
      initTimer = setTimeout(() => {
        if (!mapContainerRef.current) return;

        mapInstance = new maplibregl.Map({
          container: mapContainerRef.current,
          style: "https://api.maptiler.com/maps/streets-v2/style.json?key=Q3fXi2sghTMg1wQSFgxK",
          center: [104.083064, -3.125992],
          zoom: 12,
          attributionControl: false,
        });

        mapInstance.on('load', () => {
          mapInstance?.resize();

          // Add Satellite imagery
          mapInstance?.addSource("satellite", {
            type: "raster",
            tiles: [
              "https://api.maptiler.com/tiles/satellite-v2/{z}/{x}/{y}.jpg?key=Q3fXi2sghTMg1wQSFgxK",
            ],
            tileSize: 256,
          });
          
          mapInstance?.addLayer({
            id: "satellite-layer",
            type: "raster",
            source: "satellite",
            layout: { visibility: "visible" },
          });

          if (jalurData && jalurData.points && jalurData.points.length > 0) {
            const validPoints = jalurData.points.filter(p => 
              p.latitude != null && p.longitude != null && Number(p.latitude) !== 0 && Number(p.longitude) !== 0
            );

            if (validPoints.length === 0) return;

            const coordinates = validPoints.map(p => [p.longitude, p.latitude]);

            // Draw LineString
            const lineStringObj: GeoJSON.FeatureCollection<GeoJSON.LineString> = {
              type: "FeatureCollection",
              features: [
                {
                  type: "Feature",
                  geometry: {
                    type: "LineString",
                    coordinates: coordinates
                  },
                  properties: {}
                }
              ]
            };

            mapInstance?.addSource('jalur-source', {
              type: 'geojson',
              data: lineStringObj
            });

            // Add Line Layer
            mapInstance?.addLayer({
              id: 'jalur-layer',
              type: 'line',
              source: 'jalur-source',
              layout: {
                'line-join': 'round',
                'line-cap': 'round'
              },
              paint: {
                'line-color': '#00bcd4',
                'line-width': 4
              }
            });

            // Add Points Marker
            const pointsObj: GeoJSON.FeatureCollection<GeoJSON.Point> = {
              type: "FeatureCollection",
              features: validPoints.map(p => ({
                type: "Feature",
                geometry: {
                  type: "Point",
                  coordinates: [p.longitude, p.latitude]
                },
                properties: {
                  pokok_nbr: p.pokok_nbr,
                  bunch_count: p.bunch_count,
                  is_dead: p.is_dead,
                  color: p.is_dead ? '#f44336' : '#4caf50' 
                }
              }))
            };

            mapInstance?.addSource('points-source', {
              type: 'geojson',
              data: pointsObj
            });

            mapInstance?.addLayer({
              id: 'points-layer',
              type: 'circle',
              source: 'points-source',
              paint: {
                'circle-radius': 6,
                'circle-color': ['get', 'color'],
                'circle-stroke-width': 2,
                'circle-stroke-color': '#ffffff'
              }
            });

            // Label for Points
            mapInstance?.addLayer({
              id: 'points-label',
              type: 'symbol',
              source: 'points-source',
              layout: {
                'text-field': ['to-string', ['get', 'pokok_nbr']],
                'text-size': 10,
                'text-offset': [0, 1.5],
                'text-anchor': 'top'
              },
              paint: {
                'text-color': '#ffffff',
                'text-halo-color': '#000000',
                'text-halo-width': 1
              }
            });

            // Fit Bounds
            try {
              const boundingBox = turf.bbox(lineStringObj);
              mapInstance?.fitBounds(boundingBox as [number, number, number, number], {
                padding: 50,
                maxZoom: 18,
                animate: false
              });
            } catch (e) {
              console.error("Error setting fitBounds: ", e);
            }
          }
        });

        setMap(mapInstance);
      }, 200);
    }

    return () => {
      clearTimeout(initTimer);
      if (mapInstance) {
        mapInstance.remove();
      }
      setMap(null);
    };
  }, [isOpen, jalurData]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={{ base: "full", md: "4xl" }} isCentered>
      <ModalOverlay />
      <ModalContent borderRadius="xl" overflow="hidden">
        <ModalHeader bg="customGold.500" color="white" pr={12} fontSize="xl">
          {jalurData?.title || 'Peta Jalur Taksasi'}
        </ModalHeader>
        <ModalCloseButton color="white" />
        <ModalBody p={0}>
          <Box ref={mapContainerRef} style={{ width: '100%', height: '60vh', minHeight: '400px' }} />
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default TaksasiJalurMapModal;
